import { boolToRadio, radioToNullableBool } from "@/lib/helpers/radio-adapters";
import dayjs from "@/lib/helpers/dayjs";

import { ACAA_SCREENING_FIELDS, ACP_BOOLEAN_RADIO_FIELDS } from "../constants";
import type { NoteContent, NoteWithContent } from "../types";

export const adaptAcaaData = (note: Nullable<NoteWithContent>): Nullable<NoteWithContent> => {
  if (!note) return note; // defensive: note may be null upstream

  // shallow clone; content[item] reassigned below only
  const copyOfNote: NoteWithContent & { content: NoteContent } = {
    ...note,
    content: { ...note.content },
    // unsigned drafts never persist `signature` on the backend; default so
    // ProviderSignatureField's `!== ""` sentinel check doesn't see `undefined`
    signature: note.signature ?? "",
  };

  // Backend stores ACAA yes/no fields as booleans; form radios need
  // "Yes"/"No" strings. Maps boolean -> string for read-side use
  ACP_BOOLEAN_RADIO_FIELDS.forEach((item: string) => {
    copyOfNote.content[item] = boolToRadio(copyOfNote.content[item]);
  });

  // session_date: backend uses "MM/DD/YYYY", form field expects
  // "MMDDYYYY" digits-only. Read-side half of that round-trip.
  const sessionDate = copyOfNote.session_date;

  if (typeof sessionDate === "string" && sessionDate.includes("/")) {
    // strict mode rejects invalid calendar dates instead of rolling them over
    const parsed = dayjs(sessionDate, "MM/DD/YYYY", true);

    if (parsed.isValid()) {
      copyOfNote.session_date = parsed.format("MMDDYYYY");
    }
  }

  return copyOfNote;
};

// casts "Yes"/"No" radio strings back to booleans before submission.
// A cleared/unanswered radio ("" sentinel) drops the key entirely for most
// fields — this only fires on an unanswered draft save (Save Draft skips yup
// validation) since every other ACP_BOOLEAN_RADIO_FIELDS entry is yup-required
// on finalize. ACAA_SCREENING_FIELDS is the exception: those 4 fields must
// submit as explicit null instead of being omitted, since the backend schema
// only widens their type to nullable-boolean (vs. plain boolean) — it never
// drops the required check. That's true on every draft save, and on finalize
// specifically when patient_participant_attest is "No" (no-show).
export const adaptRadioFields = (content: NoteContent): NoteContent => {
  const copyOfContent = { ...content };

  ACP_BOOLEAN_RADIO_FIELDS.forEach((field: string) => {
    const value = radioToNullableBool(copyOfContent[field]);

    if (value === null && !ACAA_SCREENING_FIELDS.includes(field)) {
      delete copyOfContent[field];
    } else {
      copyOfContent[field] = value;
    }
  });

  return copyOfContent;
};
